import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

const ContactForm = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: false,
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: false, message: '' });

    try {
      const serviceId = 'service_4aoo48z';
      const templateId = 'template_86d2kw6';
      const publicKey = 'crffdvGUw6x5UvT8N';

      const templateParams = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone || 'Not provided',
        message: formData.message
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      setFormStatus({
        submitting: false,
        success: true,
        error: false,
        message: 'Thank you for your message! We\'ll get back to you within 24 hours.'
      });
      setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      
      setTimeout(() => {
        setFormStatus({ submitting: false, success: false, error: false, message: '' });
      }, 5000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setFormStatus({
        submitting: false,
        success: false,
        error: true,
        message: 'Oops! Something went wrong. Please try again or call us directly.'
      });
      
      setTimeout(() => {
        setFormStatus({ submitting: false, success: false, error: false, message: '' });
      }, 5000);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-5xl font-semibold text-olive-darkest mb-3">
            Contact Us
          </h2>
          <div className="w-20 h-0.5 bg-gold mx-auto mb-4"></div>
          <p className="text-text-dark text-base opacity-80 max-w-3xl mx-auto">
            Have questions or ready to schedule your appointment? We're here to help.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-heading text-2xl font-semibold text-olive-darkest mb-6">
              Get In Touch
            </h3>

            <div className="space-y-5">
              {/* Phone */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex items-start gap-4 group"
              >
                <div className="bg-olive-darkest p-3 rounded-lg group-hover:bg-gold transition-colors duration-300 flex-shrink-0">
                  <Phone className="w-5 h-5 text-white transition-colors duration-300" />
                </div>
                <div>
                  <h4 className="font-semibold text-olive-darkest mb-1 text-sm">Phone</h4>
                  <a 
                    href="tel:3325294781"
                    className="text-text-dark text-sm opacity-80 hover:text-gold transition-colors duration-300"
                  >
                    (332) 529-4781
                  </a>
                </div>
              </motion.div>

              {/* Email */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex items-start gap-4 group"
              >
                <div className="bg-olive-darkest p-3 rounded-lg group-hover:bg-gold transition-colors duration-300 flex-shrink-0">
                  <Mail className="w-5 h-5 text-white transition-colors duration-300" />
                </div>
                <div>
                  <h4 className="font-semibold text-olive-darkest mb-1 text-sm">Email</h4>
                  <a 
                    href="mailto:centuryaesthetics@gmail.com"
                    className="text-text-dark text-sm opacity-80 hover:text-gold transition-colors duration-300"
                  >
                    centuryaesthetics@gmail.com
                  </a>
                </div>
              </motion.div>

              {/* Location */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex items-start gap-4 group"
              >
                <div className="bg-olive-darkest p-3 rounded-lg group-hover:bg-gold transition-colors duration-300 flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white transition-colors duration-300" />
                </div>
                <div>
                  <h4 className="font-semibold text-olive-darkest mb-1 text-sm">Location</h4>
                  <a 
                    href="https://www.google.com/maps/place/Century+-+Longevity+%26+Aesthetics/@40.722696,-73.8571024,854m/data=!3m2!1e3!4b1!4m6!3m5!1s0x89c25f3efd11ec71:0x59b5c67e4f9c82e!8m2!3d40.722692!4d-73.8522315!16s%2Fg%2F11msbj1tpy!5m1!1e2?entry=ttu&g_ep=EgoyMDI2MDEyMS4wIKXMDSoKLDEwMDc5MjA2OUgBUAM%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-dark text-sm opacity-80 hover:text-gold transition-colors duration-300 whitespace-pre-line"
                  >
                    70-64 Yellowstone Boulevard Unit D2 Forest Hills, NY 11375
                  </a>
                </div>
              </motion.div>

              {/* Hours */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex items-start gap-4 group"
              >
                <div className="bg-olive-darkest p-3 rounded-lg group-hover:bg-gold transition-colors duration-300 flex-shrink-0">
                  <Clock className="w-5 h-5 text-white transition-colors duration-300" />
                </div>
                <div>
                  <h4 className="font-semibold text-olive-darkest mb-1 text-sm">Hours</h4>
                  <p className="text-text-dark text-sm opacity-80 whitespace-pre-line">
                    Monday: 9:00 AM - 5:00 PM{'\n'}Tuesday: 12:00 PM - 7:00 PM{'\n'}Wednesday: 8:00 AM - 4:00 PM{'\n'}Thursday: 8:00 AM - 4:00 PM{'\n'}Friday: 8:00 AM - 2:00 PM{'\n'}Saturday: 1st Saturday of Each Month, 9:00 AM - 2:00 PM
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Emergency Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-6 bg-sage-light p-5 rounded-lg"
            >
              <p className="text-olive-darkest font-semibold mb-2 text-sm">
                Emergency
              </p>
              <p className="text-text-dark opacity-80 text-xs leading-relaxed">
                For medical emergencies, please call{' '}
                <a href="tel:911" className="font-semibold text-olive-darkest hover:underline">911</a> or email{' '}
                <a 
                  href="mailto:centuryaesthetics@gmail.com" 
                  className="font-semibold text-olive-darkest hover:underline"
                >
                  centuryaesthetics@gmail.com
                </a>
              </p>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-cream p-6 rounded-lg shadow-md"
          >
            <h3 className="font-heading text-2xl font-semibold text-olive-darkest mb-5">
              Send Us a Message
            </h3>

            {/* Success/Error Messages */}
            <AnimatePresence>
              {(formStatus.success || formStatus.error) && (
                <motion.div
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  className={`mb-4 p-4 rounded-lg flex items-start gap-3 ${
                    formStatus.success 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  {formStatus.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <p className={`text-sm ${
                    formStatus.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {formStatus.message}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-text-dark mb-1.5">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={formStatus.submitting}
                    className="w-full px-4 py-2.5 text-sm border border-olive-medium/20 rounded bg-white focus:outline-none focus:border-olive-dark focus:ring-1 focus:ring-olive-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="John"
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-text-dark mb-1.5">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={formStatus.submitting}
                    className="w-full px-4 py-2.5 text-sm border border-olive-medium/20 rounded bg-white focus:outline-none focus:border-olive-dark focus:ring-1 focus:ring-olive-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-dark mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={formStatus.submitting}
                  className="w-full px-4 py-2.5 text-sm border border-olive-medium/20 rounded bg-white focus:outline-none focus:border-olive-dark focus:ring-1 focus:ring-olive-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-text-dark mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={formStatus.submitting}
                  className="w-full px-4 py-2.5 text-sm border border-olive-medium/20 rounded bg-white focus:outline-none focus:border-olive-dark focus:ring-1 focus:ring-olive-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="(555) 123-4567"
                />
                {/* SMS Consent Disclosure */}
                <p className="mt-2 text-xs text-text-dark opacity-70 leading-relaxed">
                  By entering your phone number, you consent to receive callback communications from Century Longevity & Aesthetics 
                  regarding your inquiry. We may contact you via call or text. Message & data rates may apply. Message frequency may vary. 
                  See our{' '}
                  <a href="/privacy-policy" className="text-olive-dark hover:text-gold underline">
                    Privacy Policy
                  </a>{' '}
                  for additional details.
                </p>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-text-dark mb-1.5">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  disabled={formStatus.submitting}
                  rows="4"
                  className="w-full px-4 py-2.5 text-sm border border-olive-medium/20 rounded bg-white focus:outline-none focus:border-olive-dark focus:ring-1 focus:ring-olive-dark transition-all duration-300 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="How can we help you?"
                />
              </div>

              <motion.button
                type="submit"
                disabled={formStatus.submitting}
                whileHover={!formStatus.submitting ? { y: -2 } : {}}
                whileTap={!formStatus.submitting ? { scale: 0.98 } : {}}
                className="w-full bg-olive-darkest text-cream py-3 rounded font-medium tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-olive-darkest disabled:hover:text-cream disabled:hover:shadow-lg"
              >
                {formStatus.submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Message'
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;